/**
 * ClassStatsPanel — affiche les stats d'une classe (résumé, devoirs récents,
 * compétences faibles, élèves à aider) en utilisant `/api/madrassas/classes/:cid/stats`.
 *
 * Visible aux MADRASSA_ADMIN et aux TEACHERs de la classe (ACL backend).
 */
import React, { useEffect, useState } from 'react';
import {
  Activity, AlertTriangle, BookOpen, ChevronDown, ChevronUp, Loader2,
  Sparkles, TrendingUp, Users,
} from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { madrassaApi } from '../../services/api';

const SUPPORT_META = {
  needs_help: {
    label: 'À aider',
    cls: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
  },
  needs_reinforcement: {
    label: 'À renforcer',
    cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  },
  on_track: {
    label: 'Bon niveau',
    cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  },
};

const SummaryChip = ({ label, value, testid }) => (
  <div
    className="rounded-md border border-border bg-surface-elevated/40 px-3 py-2 min-w-[120px]"
    data-testid={testid}
  >
    <p className="text-[10px] uppercase tracking-wider text-text-muted">{label}</p>
    <p className="text-base text-text-primary font-serif">
      {value === null || value === undefined ? '—' : value}
    </p>
  </div>
);

const StudentSupportRow = ({ s }) => {
  const meta = SUPPORT_META[s.status] || SUPPORT_META.on_track;
  return (
    <div
      className="rounded-md border border-border bg-surface-elevated/30 p-3"
      data-testid={`class-stats-student-${s.studentId}`}
      data-status={s.status}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <Users className="w-4 h-4 text-text-muted" />
        <span className="text-sm text-text-primary truncate">
          {s.studentName}
        </span>
        <Badge className={`${meta.cls} border text-[10px]`}>{meta.label}</Badge>
        {s.averageScore != null && (
          <span className="text-[11px] text-text-muted">
            Moy. {s.averageScore}%
          </span>
        )}
        {s.completionRate != null && (
          <span className="text-[11px] text-text-muted">
            Complétion {Math.round(s.completionRate * 100)}%
          </span>
        )}
      </div>
      {s.reasons?.length > 0 && (
        <ul className="mt-2 text-xs text-text-secondary list-disc list-inside space-y-0.5">
          {s.reasons.map((r, i) => (
            <li key={i}>{r.label}</li>
          ))}
        </ul>
      )}
      {s.recommendedActions?.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {s.recommendedActions.map((a, i) => (
            <Badge
              key={i}
              variant="outline"
              className="text-[10px]"
              data-testid={`class-stats-student-${s.studentId}-action`}
            >
              <Sparkles className="w-3 h-3 mr-1" />
              {a.label}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
};

export const ClassStatsPanel = ({ classId, autoOpen = false }) => {
  const [open, setOpen] = useState(autoOpen);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || data) return;
    setLoading(true);
    setError(null);
    madrassaApi.classStats(classId)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || 'Erreur de chargement.'))
      .finally(() => setLoading(false));
  }, [open, classId, data]);

  if (!open) {
    return (
      <Button
        size="sm"
        variant="ghost"
        onClick={() => setOpen(true)}
        data-testid={`class-stats-toggle-${classId}`}
      >
        <Activity className="w-3.5 h-3.5 mr-1" />
        Statistiques
        <ChevronDown className="w-3.5 h-3.5 ml-1" />
      </Button>
    );
  }

  return (
    <div
      className="rounded-md border border-primary/30 bg-primary/5 p-3 mt-3 space-y-3"
      data-testid={`class-stats-panel-${classId}`}
    >
      <div className="flex items-center justify-between">
        <h5 className="text-sm font-serif text-text-primary">Statistiques de la classe</h5>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}
                data-testid={`class-stats-collapse-${classId}`}>
          <ChevronUp className="w-3.5 h-3.5" />
        </Button>
      </div>
      {loading && (
        <p className="text-xs text-text-muted flex items-center gap-2">
          <Loader2 className="w-3 h-3 animate-spin" /> Calcul des statistiques…
        </p>
      )}
      {error && <p className="text-sm text-rose-300">{error}</p>}
      {data && (
        <>
          <div className="flex flex-wrap gap-2">
            <SummaryChip label="Élèves" value={data.summary.studentsCount}
                         testid="class-stats-students" />
            <SummaryChip label="Devoirs" value={data.summary.assignmentsCount}
                         testid="class-stats-assignments" />
            <SummaryChip
              label="Score moyen"
              value={data.summary.averageScore != null ? `${data.summary.averageScore}%` : null}
              testid="class-stats-avg-score"
            />
            <SummaryChip
              label="Complétion"
              value={data.summary.completionRate != null
                ? `${Math.round(data.summary.completionRate * 100)}%` : null}
              testid="class-stats-completion"
            />
            <SummaryChip
              label="À aider"
              value={data.summary.needsHelpCount}
              testid="class-stats-needs-help"
            />
            <SummaryChip
              label="À renforcer"
              value={data.summary.needsReinforcementCount}
              testid="class-stats-needs-reinforcement"
            />
            <SummaryChip
              label="Bon niveau"
              value={data.summary.onTrackCount}
              testid="class-stats-on-track"
            />
          </div>

          {/* Reading & tajweed weak skills */}
          {(data.readingSkills?.weak?.length > 0
            || data.tajweedRules?.weak?.length > 0) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {data.readingSkills?.weak?.length > 0 && (
                <div data-testid="class-stats-weak-reading">
                  <p className="text-xs uppercase tracking-wider text-text-muted mb-1">
                    Compétences lecture faibles
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {data.readingSkills.weak.map((w) => (
                      <Badge key={w.skill} variant="outline"
                             className="text-[10px] border-rose-500/30 text-rose-300">
                        <BookOpen className="w-3 h-3 mr-1" />
                        {w.skill} · {Math.round(w.accuracy * 100)}%
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              {data.tajweedRules?.weak?.length > 0 && (
                <div data-testid="class-stats-weak-tajweed">
                  <p className="text-xs uppercase tracking-wider text-text-muted mb-1">
                    Règles tajwīd faibles
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {data.tajweedRules.weak.map((w) => (
                      <Badge key={w.ruleId} variant="outline"
                             className="text-[10px] border-rose-500/30 text-rose-300">
                        {w.ruleId} · {Math.round(w.accuracy * 100)}%
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Recent assignments */}
          {data.assignments?.length > 0 && (
            <div data-testid="class-stats-assignments-list">
              <p className="text-xs uppercase tracking-wider text-text-muted mb-1">
                Devoirs récents
              </p>
              <div className="space-y-1">
                {data.assignments.slice(0, 5).map((a) => (
                  <div
                    key={a.assignmentId}
                    className="flex items-center justify-between text-xs border-b border-border/40 pb-1"
                  >
                    <span className="text-text-secondary truncate flex-1">{a.title}</span>
                    <span className="text-text-muted">
                      {a.submittedCount}/{a.totalAudience} ·{' '}
                      {a.averageScore != null ? `${a.averageScore}% moy.` : '—'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Students support */}
          <div data-testid="class-stats-support">
            <p className="text-xs uppercase tracking-wider text-text-muted mb-1 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3 text-amber-300" />
              Élèves à aider / renforcer
            </p>
            <div className="space-y-2">
              {data.studentsSupport
                .filter((s) => s.status !== 'on_track')
                .map((s) => (<StudentSupportRow key={s.studentId} s={s} />))}
              {data.studentsSupport.filter((s) => s.status !== 'on_track').length === 0 && (
                <p className="text-xs text-emerald-300 flex items-center gap-1"
                   data-testid="class-stats-all-good">
                  <TrendingUp className="w-3 h-3" />
                  Tous les élèves sont sur la bonne voie.
                </p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ClassStatsPanel;
